import { Tooltip } from "antd";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";

import BatchDelete from "@/components/Elements/BatchDelete";
import TableTitleCell from "@/components/Elements/TableTitleCell";
import { useReferentialConditionRatings } from "@/entities/referential/referential-condition-ratings";
import { REFERENTIAl_STATES_ENDPOINT } from "@/entities/referential/referential-states";
import AssetBreadcrumb from "@/modules/asset/asset-breadcrumb";
import { AssetConditionRatingTag } from "@/modules/asset/asset-condition-rating-tag";
import { AssetRiskTag } from "@/modules/asset/asset-risk-tag";
import AssetTheoricalRemainingLifespan from "@/modules/asset/asset-theorical-remaining-lifespan";
import { AssetsBatchEdit } from "@/modules/assets/assets-batch-edit";
import { AssetsListTableRowActions } from "@/modules/assets/assets-list-table";
import { useListContext } from "@/modules/list";
import ListTable from "@/modules/list-table";
import { ModelLink } from "@/modules/model-link";
import { useSidePanel } from "@/modules/side-panel";
import { StateTag } from "@/modules/state-tag";
import {
  getTableFilterQueryable,
  TABLE_FILTER_TEXT,
  useAssetsTableFilterRisk,
  useTableFilterReferentialStates,
} from "@/modules/table-filter";
import { TranslateCost } from "@/modules/translate";
import { format, fromNow } from "@/utilities/date";

const AssetsTable = ({
  showAncestorsColumns = false,
  showBatchMode = true,
  ...listTableProps
}) => {
  const { t } = useTranslation();
  const { openSidePanel } = useSidePanel();

  const { filters } = useListContext();

  const riskFilter = useAssetsTableFilterRisk();
  const statesFilter = useTableFilterReferentialStates(
    REFERENTIAl_STATES_ENDPOINT.ASSET
  );
  const { conditionRatings } = useReferentialConditionRatings();

  const onBatchEditClick = (models) => {
    const title = models[0].asset_id
      ? t("asset:form.edit.component", { count: models.length })
      : t("asset:form.edit.label", { count: models.length });

    openSidePanel({
      title,
      component: <AssetsBatchEdit assets={models} />,
    });
  };

  const onBatchDeleteClick = (models) => {
    const type = "asset";

    openSidePanel({
      title: t(type + ":delete.label"),
      component: (
        <BatchDelete
          type={type}
          models={models}
          invalidateKeys={[
            ["assets", "coordinates"],
            ["locations", "coordinates"],
          ]}
        />
      ),
    });
  };

  const batchProps = showBatchMode
    ? { onBatchEditClick, onBatchDeleteClick }
    : undefined;

  const columns = [
    {
      title: t("global:name"),
      dataIndex: "name",
      render: (_name, asset) => (
        <TableTitleCell tooltip={asset.name}>
          <ModelLink model="asset" item={asset} />
        </TableTitleCell>
      ),
      sorter: true,
      fixed: "left",
      minWidth: 220,
      ...TABLE_FILTER_TEXT,
    },
    {
      title: t("global:status"),
      dataIndex: "state",
      render: (state) => <StateTag value={state} type="asset" />,
      sorter: true,
      ...statesFilter,
    },
    {
      title: t("global:conditionRating"),
      dataIndex: "condition_rating",
      render: (_, asset) => <AssetConditionRatingTag asset={asset} />,
      sorter: true,
      filterMode: "tree",
      filters: conditionRatings?.map(({ name, rating }) => ({
        text: name,
        value: rating,
      })),
      filteredValue: filters?.condition_rating,
    },
    {
      title: t("asset:risk:label"),
      dataIndex: "risk_level",
      render: (_, asset) => <AssetRiskTag asset={asset} />,
      sorter: true,
      ...riskFilter,
    },
    {
      title: t("asset:form:nextStudy:label"),
      dataIndex: "next_study",
      render: (value) => (
        <Tooltip title={format(value, "LLLL")}>{format(value)}</Tooltip>
      ),
    },
    {
      title: t("global:reference"),
      dataIndex: "reference",
      sorter: true,
      ...TABLE_FILTER_TEXT,
    },
    {
      title: t("asset:form.replacementCost.label"),
      dataIndex: "replacement_cost",
      render: (replacement_cost) => <TranslateCost value={replacement_cost} />,
      sorter: true,
    },
    {
      title: t("asset:form.theoreticalRemainingLifespan.label"),
      dataIndex: "theoretical_remaining_lifespan_in_days",
      render: (_, asset) => <AssetTheoricalRemainingLifespan asset={asset} />,
    },
    {
      title: t("global:type"),
      dataIndex: ["asset_type", "id"],
      render: (_type_id, { asset_type }) => asset_type.name,
      sorter: true,
      key: "asset_type_id",
      sorterKey: "asset_type.name",
      ...getTableFilterQueryable({ model: "assetTypes" }),
    },
    {
      title: t("global:location"),
      dataIndex: "location_id",
      render: (_location_id, asset) => (
        <AssetBreadcrumb
          asset={asset}
          include={["location"]}
          hideIcon
          ellipsis
          compact
        />
      ),
      ...getTableFilterQueryable({ model: "locations", withContext: true }),
      hidden: !showAncestorsColumns,
    },
    {
      title: t("global:hierarchy"),
      dataIndex: "ancestors",
      render: (_ancestors, asset) => (
        <AssetBreadcrumb
          asset={asset}
          include={["asset"]}
          hideIcon
          ellipsis
          compact
        />
      ),
      hidden: !showAncestorsColumns,
    },
    {
      title: t("global:lastUpdate"),
      dataIndex: "updated_at",
      sorter: true,
      render: (value) => (
        <Tooltip title={format(value, "LLLL")}>{fromNow(value)}</Tooltip>
      ),
    },
    {
      title: "",
      fixed: "right",
      render: (asset) => <AssetsListTableRowActions asset={asset} />,
    },
  ];

  return (
    <ListTable
      modelKey="asset"
      columns={columns}
      showExportButton
      {...batchProps}
      {...listTableProps}
    />
  );
};

AssetsTable.propTypes = {
  showAncestorsColumns: PropTypes.bool,
  showBatchMode: PropTypes.bool,
};

export default AssetsTable;
